import { Box, Flex, MantineTheme, Stack, Table, Title, useMantineTheme } from '@mantine/core'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { useErrorMessages } from '@/constants/content'
import { useAuth } from '@/plugins/auth'

import { StyledStack } from '../../styled/components'
import { baseTxUrl } from '../../utils/table'
import ErrorMessage from '../ErrorMessage'
import Loading from '../Loading'
import NoData from '../NoData'
import classes from './ResponsiveTable.module.css'

interface ResponsiveTableProps {
  dashboard?: boolean
  backgroundColor?: string
  title: string
  data: any[] | null
  dataToRow: (
    t: (key: string, fallback: string) => string,
    address: `0x${string}` | string,
    transaction: any,
    collectionCreator: string,
    theme: MantineTheme,
    recent: boolean
  ) => any[]
  columns: (JSX.Element | React.ReactNode)[]
  loading: boolean
  error: any
  collectionCreator?: string
  header?: JSX.Element | React.ReactNode
  headerButton?: JSX.Element | React.ReactNode
  footer?: JSX.Element | React.ReactNode
  recent?: boolean
}

const ResponsiveTable: React.FC<ResponsiveTableProps> = ({
  dashboard = false,
  backgroundColor = '',
  title,
  data,
  dataToRow,
  collectionCreator = '',
  columns,
  loading,
  error,
  header,
  headerButton,
  footer,
  recent = false,
}) => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const errorMessages = useErrorMessages()
  const { address } = useAuth()

  return (
    <StyledStack style={{ gap: dashboard ? '4px' : '16px', height: '100%' }}>
      <Stack
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <Stack
          style={{
            flexDirection: 'row',
          }}
        >
          <Title order={2}>{title}</Title>
          <Box style={{ margin: 'auto' }}>{headerButton}</Box>
        </Stack>
        {footer}
      </Stack>
      {header}
      <Flex direction="column" gap="10px" style={{ height: '100%' }}>
        <Stack style={{ height: '100%' }}>
          {!loading ? (
            <>
              <Table
                className={classes.table}
                style={{ backgroundColor: backgroundColor || theme.colors.background[0] }}
                withColumnBorders={false}
              >
                <thead>
                  <tr>
                    {columns.map((column, index) => (
                      <td key={`${String(index)}`} className={classes.tableHeading}>
                        {column}
                      </td>
                    ))}
                  </tr>
                </thead>
                {data && data.length > 0 && (
                  <tbody>
                    {data.map((elem, index) => (
                      <tr key={`${elem}${String(index)}`}>
                        {dataToRow(
                          t,
                          address?.toLocaleLowerCase() || '',
                          elem,
                          collectionCreator,
                          theme,
                          recent
                        ).map((row, index2) => (
                          <td
                            key={`${row}${String(index2)}`}
                            className={classes.tableCell}
                            onClick={() => {
                              window.open(`${baseTxUrl}${elem.txnHash}`, '_blank')
                            }}
                          >
                            {index2 === 0 || index2 === 1 || index2 === 3 ? (
                              <div className={classes.tableCellAddress}>
                                {React.cloneElement(row, {
                                  onClick: (event: any) => event.stopPropagation(),
                                })}
                              </div>
                            ) : (
                              row
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                )}
              </Table>
              {!(data && data.length > 0) && !error && <NoData missing="activity" />}
            </>
          ) : (
            <Loading />
          )}
          {error && (
            <ErrorMessage message={errorMessages.indexerConnection} details={error.message} />
          )}
        </Stack>
      </Flex>
    </StyledStack>
  )
}

export default ResponsiveTable
